/**
 * MessageHandler - Base class for handling data channel messages
 *
 * Provides:
 * - Schema validation
 * - Automatic throttling/debouncing
 * - Error recovery with retries
 * - Type safety via JSDoc
 * - Shallow comparison for change detection
 *
 * @example
 * class RobotStatusHandler extends MessageHandler {
 *   constructor(eventBus) {
 *     super('robot.status', eventBus, {
 *       throttle: 100,
 *       schema: RobotStatusSchema
 *     });
 *   }
 *
 *   handle(message) {
 *     if (this.hasChanged(message)) {
 *       this.publish(`robot:${message.robot_info.robot_id}:status`, message);
 *     }
 *   }
 * }
 */

class MessageHandler {
  /**
   * Create a MessageHandler
   * @param {string} messageType - Type of message this handler processes
   * @param {EventBus} eventBus - EventBus instance for publishing events
   * @param {Object} [options] - Handler configuration
   * @param {number} [options.throttle=0] - Throttle interval in ms (0 = no throttle)
   * @param {number} [options.debounce=0] - Debounce interval in ms (0 = no debounce)
   * @param {Function} [options.schema] - Validation schema function
   * @param {Function} [options.logger=console] - Logger instance
   * @param {boolean} [options.trackChanges=true] - Track changes for hasChanged()
   * @param {number} [options.maxRetries=0] - Max retries on error
   * @param {boolean} [options.enableDebug=false] - Enable debug logging
   */
  constructor(messageType, eventBus, options = {}) {
    this.messageType = messageType;
    this.eventBus = eventBus;

    // Configuration
    this.throttle = options.throttle || 0;
    this.debounce = options.debounce || 0;
    this.schema = options.schema || null;
    this.logger = options.logger || console;
    this.trackChanges = options.trackChanges !== false;
    this.maxRetries = options.maxRetries || 0;
    this.enableDebug = options.enableDebug || false;

    // State
    this.lastMessage = null;
    this.lastProcessedTime = 0;
    this.throttleTimer = null;
    this.debounceTimer = null;
    this.pendingMessage = null;
    this.retryCount = 0;

    // Statistics
    this.stats = {
      messagesReceived: 0,
      messagesProcessed: 0,
      messagesThrottled: 0,
      validationErrors: 0,
      processingErrors: 0
    };

    if (this.enableDebug) {
      this.logger.debug(`[MessageHandler:${messageType}] Initialized`);
    }
  }

  /**
   * Process incoming message (applies throttling/debouncing)
   * @param {*} message - Raw message data
   * @returns {boolean} True if message was processed
   */
  process(message) {
    this.stats.messagesReceived++;

    // Validate message
    if (!this.validate(message)) {
      return false;
    }

    // Apply debouncing (wait for silence before processing)
    if (this.debounce > 0) {
      return this._processDebounced(message);
    }

    // Apply throttling (rate limiting)
    if (this.throttle > 0) {
      return this._processThrottled(message);
    }

    // Process immediately
    return this._processImmediate(message);
  }

  /**
   * Validate message against schema
   * @param {*} message - Message to validate
   * @returns {boolean} True if valid
   */
  validate(message) {
    if (!this.schema) {
      return true; // No schema = always valid
    }

    try {
      const result = this.schema(message);
      if (!result) {
        this.stats.validationErrors++;
        this.logger.warn(`[MessageHandler:${this.messageType}] Validation failed:`, message);
        this.eventBus.publish('message:validation:error', {
          messageType: this.messageType,
          message,
          error: 'Schema validation failed'
        });
        return false;
      }
      return true;
    } catch (error) {
      this.stats.validationErrors++;
      this.logger.error(`[MessageHandler:${this.messageType}] Validation error:`, error);
      this.eventBus.publish('message:validation:error', {
        messageType: this.messageType,
        message,
        error: error.message
      });
      return false;
    }
  }

  /**
   * Handle validated message (override in subclass)
   * @param {*} message - Validated message
   */
  handle(message) {
    // Default implementation - publish to event bus
    this.publish(`message:${this.messageType}`, message);
  }

  /**
   * Publish event via EventBus
   * @param {string} eventName - Event name
   * @param {*} payload - Event payload
   */
  publish(eventName, payload) {
    if (this.eventBus) {
      this.eventBus.publish(eventName, payload);
    }
  }

  /**
   * Check if message has changed from last message (shallow comparison)
   * @param {Object} message - New message
   * @param {Array<string>} [keys] - Specific keys to compare (defaults to all)
   * @returns {boolean} True if message has changed
   */
  hasChanged(message, keys = null) {
    if (!this.trackChanges || !this.lastMessage) {
      return true;
    }

    const compareKeys = keys || Object.keys(message);

    for (const key of compareKeys) {
      if (this.lastMessage[key] !== message[key]) {
        return true;
      }
    }

    return false;
  }

  /**
   * Deep equality check for nested objects
   * @param {Object} obj1 - First object
   * @param {Object} obj2 - Second object
   * @returns {boolean} True if objects are equal
   */
  deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!this.deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }

  /**
   * Get statistics
   * @returns {Object} Handler statistics
   */
  getStats() {
    return {
      ...this.stats,
      messageType: this.messageType,
      throttle: this.throttle,
      debounce: this.debounce
    };
  }

  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      messagesReceived: 0,
      messagesProcessed: 0,
      messagesThrottled: 0,
      validationErrors: 0,
      processingErrors: 0
    };
  }

  /**
   * Clear state and timers
   */
  clear() {
    this.lastMessage = null;
    this.pendingMessage = null;
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  // ============== Private Methods ==============

  /**
   * Process message immediately
   * @private
   */
  _processImmediate(message) {
    try {
      this.handle(message);
      this.lastMessage = message;
      this.stats.messagesProcessed++;
      this.retryCount = 0;
      return true;
    } catch (error) {
      return this._handleError(error, message);
    }
  }

  /**
   * Process message with throttling (rate limiting)
   * @private
   */
  _processThrottled(message) {
    const now = Date.now();
    const timeSinceLastProcess = now - this.lastProcessedTime;

    // If enough time has passed, process immediately
    if (timeSinceLastProcess >= this.throttle) {
      this.lastProcessedTime = now;
      return this._processImmediate(message);
    }

    // Otherwise, schedule for later
    this.stats.messagesThrottled++;
    this.pendingMessage = message;

    if (!this.throttleTimer) {
      const remainingTime = this.throttle - timeSinceLastProcess;
      this.throttleTimer = setTimeout(() => {
        this.throttleTimer = null;
        if (this.pendingMessage) {
          const msg = this.pendingMessage;
          this.pendingMessage = null;
          this.lastProcessedTime = Date.now();
          this._processImmediate(msg);
        }
      }, remainingTime);
    }

    return false;
  }

  /**
   * Process message with debouncing (wait for silence)
   * @private
   */
  _processDebounced(message) {
    this.pendingMessage = message;

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.pendingMessage) {
        const msg = this.pendingMessage;
        this.pendingMessage = null;
        this._processImmediate(msg);
      }
    }, this.debounce);

    return false;
  }

  /**
   * Handle processing error with retry logic
   * @private
   */
  _handleError(error, message) {
    this.stats.processingErrors++;
    this.logger.error(`[MessageHandler:${this.messageType}] Processing error:`, error);

    this.eventBus.publish('message:processing:error', {
      messageType: this.messageType,
      message,
      error: error.message,
      retryCount: this.retryCount
    });

    // Retry if configured
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      if (this.enableDebug) {
        this.logger.debug(`[MessageHandler:${this.messageType}] Retrying (${this.retryCount}/${this.maxRetries})`);
      }
      setTimeout(() => {
        this._processImmediate(message);
      }, 100 * this.retryCount); // Exponential backoff
      return false;
    }

    this.retryCount = 0;
    return false;
  }
}

export default MessageHandler;
